import React from 'react'
import { Tooltip } from '@mui/material'
import { getInfoBadgeClassName } from '../../ui/InfoBadge'
import { formatAppendixRefTooltip, type ResolvedAppendixRef } from '../../../utils/appendixRefs'

type AppendixRefBadgesProps = {
  refs: ReadonlyArray<ResolvedAppendixRef>
  onOpen: () => void
  // Letters shown before the rest collapse into a "+N" badge.
  maxVisible?: number
  className?: string
}

const BADGE_CLASS = 'cursor-pointer rounded-full px-1.5 py-0 text-[10px] leading-4 hover:bg-[#C7DEF6]'

/**
 * Letter pills for the appendices that apply to a code item, value or group. Inherited references
 * render outlined so they read lighter than the ones set on the entity itself. Clicking anything
 * opens the referenced-appendices list for that entity.
 */
export const AppendixRefBadges: React.FC<AppendixRefBadgesProps> = ({ refs, onOpen, maxVisible = 2, className = '' }) => {
  if (refs.length === 0) return null

  const visible = refs.slice(0, maxVisible)
  const hidden = refs.length - visible.length

  const open = (event: React.MouseEvent) => {
    event.stopPropagation()
    onOpen()
  }

  return (
    <Tooltip title={<span className="whitespace-pre-line">{formatAppendixRefTooltip(refs)}</span>}>
      <span
        className={`inline-flex shrink-0 items-center gap-1 ${className}`}
        data-testid="appendix-ref-badges"
        onClick={open}
        onDoubleClick={(event) => event.stopPropagation()}
      >
        {visible.map(ref => (
          <button
            key={ref.key}
            type="button"
            aria-label={`Appendix ${ref.letter}: ${ref.sourceLabel}`}
            className={getInfoBadgeClassName('info', `${BADGE_CLASS} ${ref.source === 'own' ? 'font-semibold' : 'bg-white font-medium'}`)}
            onClick={open}
          >
            {ref.letter}
          </button>
        ))}
        {hidden > 0 ? (
          <button
            type="button"
            aria-label={`${hidden} more referenced ${hidden === 1 ? 'appendix' : 'appendices'}`}
            className={getInfoBadgeClassName('neutral', `${BADGE_CLASS} hover:bg-[#D1D5DB]`)}
            onClick={open}
          >
            +{hidden}
          </button>
        ) : null}
      </span>
    </Tooltip>
  )
}

export default AppendixRefBadges
